import React, { useState, useEffect } from 'react';
import { Menu, X, Mail, Calendar, User, Compass, Sparkles, Heart, Home, Globe } from 'lucide-react';
import { Language } from '../types';

const LogoEmblem = () => (
  <div className="relative w-10 h-10 group" id="logo">
    <div className="absolute inset-0 bg-amber-400/20 blur-xl rounded-full group-hover:bg-amber-400/40 transition-all duration-700" />
    <svg viewBox="0 0 40 40" className="w-full h-full relative z-10 animate-[spin_20s_linear_infinite]">
      <path d="M22 10C15.3726 10 10 15.3726 10 22C10 28.6274 15.3726 34 22 34C24.4682 34 26.7562 33.2536 28.6517 31.9774C23.1554 31.5414 18.8 26.9405 18.8 21.3333C18.8 15.7262 23.1554 11.1252 28.6517 10.6892C26.7562 9.41303 24.4682 8.66663 22 8.66663V10Z" fill="url(#gold-grad)" />
      <path d="M28 22L30 15L32 22L39 24L32 26L30 33L28 26L21 24L28 22Z" fill="white" className="animate-pulse" />
      <defs>
        <linearGradient id="gold-grad" x1="10" x2="34" y2="34" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FDE68A" /><stop offset="0.5" stopColor="#F59E0B" /><stop offset="1" stopColor="#D97706" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

interface HeaderProps {
  onHomeClick: () => void;
  onBookClick: () => void;
  onNewsletterClick: () => void;
  onMemberPortalClick: () => void;
  isSubPage?: boolean;
  currentLang: Language;
  onLangChange: (lang: Language) => void;
}

const Header: React.FC<HeaderProps> = ({ onHomeClick, onBookClick, onNewsletterClick, onMemberPortalClick, isSubPage, currentLang, onLangChange }) => {
  const [scrolled, setScrolled] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const LANGUAGES: Array<{ code: Language; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Español' },
    { code: 'zh', label: '中文' },
    { code: 'ja', label: '日本語' },
    { code: 'ru', label: 'Русский' },
    { code: 'de', label: 'Deutsch' },
  ];

  const navItems = [
    { name: 'Offerings', href: '#services', icon: Compass },
    { name: 'Circles', href: '#membership', icon: Heart },
    { name: 'Community', href: '#community', icon: Home },
  ];

  const handleNavClick = (e: React.MouseEvent, href: string) => {
    e.preventDefault();
    if (isSubPage) {
      onHomeClick();
      setTimeout(() => {
        const element = document.querySelector(href);
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    } else {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-[100000] transition-all duration-500 border-b ${
        scrolled 
          ? 'bg-slate-950/95 backdrop-blur-xl py-2 border-amber-500/10 shadow-[0_4px_20px_rgba(0,0,0,0.5)]' 
          : 'bg-slate-950/60 backdrop-blur-sm py-4 border-indigo-900/20'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onHomeClick} className="flex items-center space-x-4 cursor-pointer group text-left">
              <LogoEmblem />
              <div className="flex flex-col">
                <span className="text-[9px] uppercase tracking-[0.5em] text-amber-500 font-bold leading-none mb-1">Astrology</span>
                <span className="text-xl md:text-2xl font-serif font-bold tracking-widest text-transparent bg-clip-text bg-gradient-to-r from-slate-50 via-amber-100 to-slate-50 group-hover:from-white group-hover:to-white transition-all duration-700">BY ALLIE</span>
              </div>
            </button>
            
            <nav className="hidden lg:flex items-center space-x-6">
              <div className="relative">
                <button 
                  id="header-lang-btn"
                  onClick={() => setShowLangMenu(!showLangMenu)}
                  className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-indigo-300 hover:text-amber-400 transition-colors p-2"
                >
                  <Globe size={14} /> {currentLang}
                </button>
                {showLangMenu && (
                  <div className="absolute top-full mt-2 right-0 bg-slate-900 border border-indigo-800 shadow-2xl rounded-sm p-2 grid grid-cols-2 gap-2 w-48 animate-fade-in">
                    {LANGUAGES.map(l => (
                      <button 
                        key={l.code}
                        onClick={() => { onLangChange(l.code); setShowLangMenu(false); }}
                        className={`text-[9px] uppercase tracking-widest p-2 rounded-sm transition-all text-left ${currentLang === l.code ? 'bg-amber-500 text-slate-950 font-bold' : 'text-indigo-200 hover:bg-indigo-950'}`}
                      >
                        {l.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {navItems.map((item) => (
                <a 
                  key={item.name} 
                  href={item.href} 
                  onClick={(e) => handleNavClick(e, item.href)} 
                  className="text-[10px] uppercase tracking-[0.3em] text-indigo-200 hover:text-amber-400 transition-all cursor-pointer"
                >
                  {item.name}
                </a>
              ))}
              <button id="header-portal" onClick={onMemberPortalClick} className="text-[10px] uppercase tracking-[0.3em] text-amber-200 hover:text-white transition-all flex items-center gap-2 border border-amber-500/30 px-3 py-1.5 rounded-sm bg-slate-900/40"><User size={12} /> Sign In</button>
              <button id="header-book-btn" onClick={onBookClick} className="bg-amber-500 text-slate-950 px-5 py-2.5 rounded-sm text-[10px] uppercase tracking-widest font-bold hover:bg-amber-400 transition-all flex items-center gap-2 shadow-lg shadow-amber-500/10"><Calendar size={12} /> Readings</button>
            </nav>
          </div>
        </div>
      </header>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-[1000001] bg-[#020617] border-t border-amber-500/40 shadow-[0_-8px_30px_rgba(0,0,0,0.9)] pb-[env(safe-area-inset-bottom, 0)] h-20 flex justify-around items-center px-2">
        {navItems.map((item) => (
          <a 
            key={item.name} 
            href={item.href} 
            onClick={(e) => handleNavClick(e, item.href)} 
            className="flex flex-col items-center justify-center gap-1.5 px-3 transition-all text-indigo-200 active:text-amber-400 active:scale-90"
          >
            <item.icon size={22} className="drop-shadow-[0_0_10px_rgba(129,140,248,0.3)]" />
            <span className="text-[9px] uppercase tracking-[0.2em] font-bold">{item.name}</span>
          </a>
        ))}
        <button onClick={onBookClick} className="flex flex-col items-center justify-center gap-1.5 px-3 text-amber-500 active:scale-90">
          <div className="p-3 bg-amber-500 text-slate-950 rounded-full -mt-10 shadow-[0_0_25px_rgba(245,158,11,0.6)] border-4 border-[#020617] ring-2 ring-amber-500/20"><Calendar size={26} /></div>
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold mt-1">Book</span>
        </button>
        <button onClick={onMemberPortalClick} className="flex flex-col items-center justify-center gap-1.5 px-3 text-indigo-200 active:text-amber-400 active:scale-90">
          <User size={22} className="drop-shadow-[0_0_10px_rgba(129,140,248,0.3)]" />
          <span className="text-[9px] uppercase tracking-[0.2em] font-bold">Portal</span>
        </button>
      </nav>
    </>
  );
};

export default Header;